import { computed, ComputedRef } from "vue";
import { useStore } from "vuex";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import { formatDate } from "@/shared-helpers/date-helper";
import { formatNumber } from "@/apps/credit-line/helpers/formatNumber.helper";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";
import { calculateEffectiveInterestRate } from "@/shared-helpers/effective-interest-rate.helper";
import {
  AreValidationsRequiredResponse,
  NationalIdValidationResponse,
} from "../models/IKycValidations";
import {
  IFeeSchema,
  IWithdrawalEstimate,
  IWithdrawalEstimateField,
} from "@/apps/credit-line/models/IWithdrawalEstimate";

export function useWithdrawalEstimation(): {
  country: ComputedRef<CL_COUNTRY | null>;
  withdrawalValidation: ComputedRef<AreValidationsRequiredResponse | null>;
  withdrawalEstimate: ComputedRef<IWithdrawalEstimate | null>;
  selectedWithdrawMethod: ComputedRef<any>;
  needAmlSurvey: ComputedRef<boolean>;
  paymentTerm: ComputedRef<number>;
  withdrawalTotalPayment: ComputedRef<string>;
  nextPaymentDueDate: ComputedRef<string>;
  weeklyInterestRate: ComputedRef<string>;
  weeklyPaymentAmount: ComputedRef<string>;
  wasValidationSuccessful: ComputedRef<boolean>;
  curpValidationResult: ComputedRef<NationalIdValidationResponse | null>;
  annualInterestRate: ComputedRef<string>;
  feeSchema: ComputedRef<IFeeSchema | null>;
  totalInterest: ComputedRef<string>;
  withdrawalEstimateFields: ComputedRef<IWithdrawalEstimateField[] | null>;
} {
  const store = useStore();
  const { locale } = useI18n();

  // Raw store getters
  const country = computed(
    (): CL_COUNTRY | null => store.getters["creditLine/getCountry"]
  );

  const withdrawalValidation = computed(
    (): AreValidationsRequiredResponse | null =>
      store.getters["creditLine/getWithdrawalValidationResponse"]
  );

  const withdrawalEstimate = computed<IWithdrawalEstimate | null>(() => {
    return store.getters["creditLine/getWithdrawalEstimate"];
  });

  const withdrawalEstimateFields = computed<IWithdrawalEstimateField[] | null>(
    () => {
      return store.getters["creditLine/getWithdrawalEstimateFields"];
    }
  );

  const selectedWithdrawMethod = computed(
    () => store.getters["creditLine/getSelectedWithdrawMethod"]
  );

  const needAmlSurvey = computed((): boolean =>
    Boolean(store.getters["creditLine/getAmlSurveyData"]?.needSurvey)
  );

  const wasValidationSuccessful = computed((): boolean =>
    Boolean(store.getters["creditLine/wasValidationSuccessful"])
  );

  const curpValidationResult = computed(() => {
    return store.getters["creditLine/getCurpValidationResult"];
  });

  // Computed properties based on withdrawal estimate
  const paymentTerm = computed<number>(
    () => withdrawalEstimate.value?.paymentTerm || 0
  );

  const withdrawalTotalPayment = computed<string>(() =>
    withdrawalEstimate.value
      ? currencyFormat(withdrawalEstimate.value.withdrawalTotalPayment)
      : ""
  );

  const nextPaymentDueDate = computed<string>(() =>
    withdrawalEstimate.value
      ? formatDate(
          withdrawalEstimate.value.nextPaymentDueDate,
          locale.value.replace("_", "-")
        )
      : ""
  );

  const weeklyInterestRate = computed<string>(() => {
    if (!withdrawalEstimate.value) return "";
    const apr = Number(withdrawalEstimate.value.annualPercentageRate);
    if (country.value === CL_COUNTRY.CO) {
      const effectiveRate = calculateEffectiveInterestRate(apr);
      return formatNumber(effectiveRate / 52);
    }
    return formatNumber(apr / 52);
  });

  const weeklyPaymentAmount = computed<string>(() =>
    withdrawalEstimate.value
      ? currencyFormat(withdrawalEstimate.value.newMinimumPaymentAmount)
      : ""
  );

  const feeSchema = computed<IFeeSchema | null>(
    () => withdrawalEstimate.value?.feeSchema || null
  );

  const totalInterest = computed<string>(() => {
    if (!withdrawalEstimate.value?.withdrawalTotalPayment) {
      return "";
    }

    const totalPayment = Number(
      withdrawalEstimate.value.withdrawalTotalPayment
    );
    const principal = withdrawalEstimate.value.amount;
    const availabilityFee =
      feeSchema.value?.availabilityFee?.totalIncludingTax ?? 0;
    const insuranceFee =
      feeSchema.value?.insuranceFee?.totalIncludingCommission ?? 0;

    const interest = totalPayment - principal - availabilityFee - insuranceFee;

    return currencyFormat(interest);
  });

  const annualInterestRate = computed(() => {
    const apr = withdrawalEstimate.value?.annualPercentageRate ?? 0;
    if (country.value === CL_COUNTRY.CO) {
      const effectiveRate = calculateEffectiveInterestRate(Number(apr));
      return formatNumber(effectiveRate * 100);
    }
    return formatNumber(Number(apr) * 100);
  });

  return {
    // Raw data
    country,
    feeSchema,
    needAmlSurvey,
    annualInterestRate,
    withdrawalEstimate,
    withdrawalValidation,
    selectedWithdrawMethod,

    // Formatted data
    paymentTerm,
    totalInterest,
    nextPaymentDueDate,
    weeklyInterestRate,
    weeklyPaymentAmount,
    curpValidationResult,
    withdrawalTotalPayment,
    wasValidationSuccessful,
    withdrawalEstimateFields,
  };
}
